import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { DevTool } from '@hookform/devtools'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { z } from 'zod'

import { Checkbox, Input } from '@repo/form'
import { useModalConfirm } from '@repo/ui'
import { cn } from '@repo/utils'

import { ageInYears } from 'global-utils'
import { getImageURL, IMAGE_SIZES } from 'images'
import { Info, ProgressBar } from 'ui'
import { useFetch, useMutate } from 'utils'

import { authLogout, authSetAvatar } from 'actions/AuthActions'
import moment from 'moment/moment'

import AvatarEdit from './AvatarEdit'

import type { SettingsSchema } from 'users'
import type { ServerResponse } from 'utils'

const SettingsUpdateForm = z
  .object({
    first_name: z.string().min(1, { message: 'El nombre es requerido' }),
    height: z.number().optional().nullish(),
    key_code: z.string().optional().nullish(),
    last_name: z.string().min(1, { message: 'El apellido es requerido' }),
    nin: z.string().optional().nullish(),
    password_actual: z.string().optional(),
    phone: z.string().optional().nullish(),
    weight: z.number().optional().nullish(),

    password: z.string().optional().nullish(),
    password_confirmation: z.string().optional().nullish(),
  })
  .superRefine((val, ctx) => {
    if (val.phone && !/^3\d{9}$/.test(val.phone)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'El teléfono debe tener 10 dígitos.',
        path: ['phone'],
      })
    }
    if (val.weight && val.weight <= 20) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'El peso no es válido.',
        path: ['weight'],
      })
    }
    if (val.height && val.height <= 50) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'El peso no es válido.',
        path: ['height'],
      })
    }
    if (val.password_actual || val.key_code) {
      if (val.password_actual && (val.password_actual.length < 6 || val.password_actual.length > 20)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            val.password_actual.length < 6
              ? 'La contraseña debe tener al menos 6 caracteres'
              : 'La contraseña debe tener como máximo 20 caracteres',
          path: ['password_actual'],
        })
      }
      if (val.key_code && val.key_code.length !== 6) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'El código enviado no es válido',
          path: ['key_code'],
        })
      }
    } else {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Un método de confirmación es requerido',
        path: ['password_actual'],
      })
    }

    if (val.password || val.password_confirmation) {
      if (!val.password || val.password.length < 6 || val.password.length > 20) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            !val.password || val.password.length < 6
              ? 'La nueva contraseña debe tener al menos 6 caracteres'
              : 'La nueva contraseña debe tener como máximo 20 caracteres',
          path: ['password'],
        })
      }
      if (val.password !== val.password_confirmation) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Las contraseñas no concuerdan',
          path: ['password_confirmation'],
        })
      }
    }
  })

type SettingsUpdateForm = z.infer<typeof SettingsUpdateForm>

const Settings = () => {
  const dispatch = useDispatch()
  const [deleteData, setDeleteData] = useState(false)
  const user = useSelector((state: { auth: { usr: { email: string } } }) => state.auth.usr)
  const image = useSelector((state: { auth: { usr: { avatar: string } } }) => state.auth.usr.avatar)
  const settings = useFetch<ServerResponse<SettingsSchema[]>>('user/settings', {
    enabled: false,
    queryKey: ['settings'],
  })
  const settingsUpdate = useMutate<SettingsUpdateForm, ServerResponse<SettingsSchema[]>>({
    method: 'put',
    url: 'user/settings',
  })
  const accountDelete = useMutate<SettingsUpdateForm>({
    method: 'put',
    url: 'user/deleteAccount',
  })
  const [ModalConfirm, confirmSave] = useModalConfirm()
  const profile = settings.data?.data?.[0]

  const imageURL = image ? getImageURL(image, IMAGE_SIZES.FIT_100_100) : undefined

  const form = useForm<SettingsUpdateForm>({
    defaultValues: async () => {
      const result = await settings.refetch()
      return {
        first_name: '',
        height: 0,
        key_code: '',
        last_name: '',
        nin: '',
        password: '',
        password_actual: '',
        password_confirmation: '',
        phone: '',
        weight: 0,
        ...result.data?.data?.[0],
      }
    },
    mode: 'onBlur',
    resolver: zodResolver(SettingsUpdateForm), // Apply the zodResolver
  })

  const onSave = async (data: SettingsUpdateForm) => {
    try {
      if (await confirmSave()) {
        if (deleteData) {
          await accountDelete.mutateAsync(data)
          setTimeout(() => {
            dispatch(authLogout())
          }, 3000)
        } else {
          // @ts-ignore
          const result = await settingsUpdate.mutateAsync(data)
          if (result?.data?.[0]) {
            form.reset(result.data[0])
            dispatch(authSetAvatar(result.data[0].avatar?.filename))
          }
        }
      }
    } catch (error: unknown) {
      /* empty */
    }
  }

  return (
    <div className="Settings p-0 375:p-2">
      <div className="relative bg-white mt-9 rounded-md p-2">
        <div className="text-greyDark size-20 -mt-10 ml-1 border-4 border-greyLightest bg-greyLightest rounded-full">
          <div className="size-full border border-white bg-greyLightest rounded-full overflow-hidden shadow">
            {imageURL ? (
              <img alt="avatar" className="size-full" src={imageURL} />
            ) : (
              <svg className="size-full" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 18c-4.411 0-8-3.589-8-8 0-1.168.258-2.275.709-3.276.154.09.308.182.456.276.396.25.791.5 1.286.688.494.187 1.088.312 1.879.312.792 0 1.386-.125 1.881-.313s.891-.437 1.287-.687.792-.5 1.287-.688c.494-.187 1.088-.312 1.88-.312s1.386.125 1.88.313c.495.187.891.437 1.287.687s.792.5 1.287.688c.178.067.374.122.581.171.191.682.3 1.398.3 2.141 0 4.411-3.589 8-8 8z" />
                <circle cx="8.5" cy="12.5" r="1.5" />
                <circle cx="15.5" cy="12.5" r="1.5" />
              </svg>
            )}
          </div>
        </div>
        {!profile && <ProgressBar value={100} />}
        <FormProvider {...form}>
          <form
            autoComplete="off"
            className="flex flex-wrap items-start gap-2"
            noValidate
            onSubmit={form.handleSubmit(onSave)}
          >
            <div className="flex justify-center w-col-1">
              <AvatarEdit />
            </div>
            <div className="flex-1 flex flex-wrap items-start gap-2 *:w-col-1">
              <Input
                {...form.register('first_name')}
                error={form.formState.errors.first_name?.message}
                label="Nombres"
                required
              />
              <Input
                {...form.register('last_name')}
                error={form.formState.errors.last_name?.message}
                label="Apellidos"
                required
              />
              <div className="!w-full font-bold px-3 py-2 border-b text-lg">Información de identificación</div>
              <Input
                {...form.register('nin')}
                className="!w-col-0.5"
                error={form.formState.errors.nin?.message}
                label="No. Identificación"
                labelInfo="Se usa para buscarte en los torneos"
              />
              <Input
                {...form.register('phone')}
                className="!w-col-0.5"
                error={form.formState.errors.phone?.message}
                label="No. de teléfono"
              />
              <Input disabled label="Email" name="email" type="email" value={user.email} />
            </div>
            <div className="flex flex-wrap items-start gap-2 *:w-col-0.5 w-full">
              <div className="!w-full font-bold px-3 py-2 border-b text-lg">Información deportiva</div>
              <Input
                {...form.register('weight', { valueAsNumber: true })}
                error={form.formState.errors.weight?.message}
                label="Peso"
                labelComment="(kg)"
                type="number"
              />
              <Input
                {...form.register('height', { valueAsNumber: true })}
                error={form.formState.errors.height?.message}
                label="Estatura"
                labelComment="(cm)"
                type="number"
              />
              <div className="flex flex-col gap-1">
                <label className="font-bold" htmlFor="gender">
                  Género deportivo
                </label>
                <div>
                  <select
                    id="gender"
                    className="block w-full outline-0 rounded-md border-0 shadow-sm ring-1 ring-inset ring-greyLight placeholder:text-grey focus:ring-1 focus:ring-inset focus:ring-grey px-3 py-2 text-inherit"
                    disabled
                    value={profile?.gender || ''}
                  >
                    <option value={'F'}>Femenino</option>
                    <option value={'M'}>Masculino</option>
                  </select>
                </div>
              </div>
              <Input
                className="!w-col-1"
                disabled
                label="Edad"
                name="age"
                transparent
                value={
                  profile?.born_at
                    ? `${ageInYears(profile.born_at)} años (${moment.unix(profile.born_at).utc().format('YYYY-MM-DD')})`
                    : ''
                }
              />
            </div>
            <div className="flex flex-wrap items-start gap-2 *:w-col-1 w-full">
              <div className="!w-full font-bold px-3 py-2 border text-lg bg-orangeLightest rounded-md">
                <InformationCircleIcon className="size-4 align-middle text-orange" /> Cambiar la clave de acceso
                <div className="font-normal px-1.5 py-1">
                  Llena los campos para cambiar tu contraseña. Si no deseas cambiarla, déjalos en blanco.
                </div>
              </div>
              <Input
                {...form.register('password')}
                error={form.formState.errors.password?.message}
                label="Nueva contraseña"
                type="password"
              />
              <Input
                {...form.register('password_confirmation')}
                error={form.formState.errors.password_confirmation?.message}
                label="Confirmación de contraseña"
                type="password"
              />
            </div>
            <div className="flex flex-wrap items-start gap-2 *:w-col-1 w-full">
              <div className="!w-full font-bold px-3 py-2 border-b text-lg">Habeas Data</div>
              <Checkbox
                checked={deleteData}
                contentClassName="bg-redLightest"
                label="Eliminar mi cuenta"
                name="delete_data"
                onChange={() => setDeleteData(!deleteData)}
              >
                Eliminar toda la información relacionada directamente con mi cuenta.
              </Checkbox>
            </div>
            <div className="flex flex-wrap items-start gap-2 *:w-col-1 w-full">
              <div className="!w-full font-bold px-3 py-2 border-b text-lg">Usa una confirmación de seguridad</div>
              <Input
                {...form.register('password_actual')}
                error={form.formState.errors.password_actual?.message}
                label="Contraseña actual"
                placeholder="Contraseña 6+"
                type="password"
              />
            </div>
            <div className="!w-full font-bold px-3 py-2 border-b text-lg" />
            <div className="flex flex-wrap flex-col-reverse items-start justify-start gap-2 600:flex-row">
              {deleteData ? (
                <button
                  className={cn(
                    'inline-flex justify-center rounded-md px-3 py-2 text-base border border-red text-white shadow-sm hover:opacity-85',
                    settingsUpdate.isPending ? 'bg-orange' : 'bg-red',
                  )}
                  disabled={settingsUpdate.isPending}
                  type="submit"
                >
                  Eliminar mi cuenta
                </button>
              ) : (
                <button
                  className={cn(
                    'inline-flex justify-center rounded-md px-3 py-2 text-base border border-green text-white shadow-sm hover:opacity-85',
                    settingsUpdate.isPending ? 'bg-orange' : 'bg-green',
                  )}
                  disabled={settingsUpdate.isPending}
                  type="submit"
                >
                  Guardar cambios
                </button>
              )}

              {settingsUpdate.isError && (
                <Info accent="red" basic icon="warning sign">
                  {settingsUpdate.error?.response?.data?.error?.message || 'Error al guardar los cambios'}
                </Info>
              )}
              {settingsUpdate.isSuccess && (
                <Info accent="green" basic icon="checkmark">
                  Cambios guardados correctamente
                </Info>
              )}
              {accountDelete.isError && (
                <Info accent="red" basic icon="warning sign">
                  {accountDelete.error?.response?.data?.error?.message || 'Error al eliminar la cuenta'}
                </Info>
              )}
              {accountDelete.isSuccess && (
                <Info accent="green" basic icon="checkmark">
                  Cuenta eliminada correctamente. Esperamos que regreses pronto.
                </Info>
              )}
            </div>
            <ModalConfirm
              accent={deleteData ? 'red' : 'green'}
              title={deleteData ? 'Eliminar la cuenta y toda la información personal' : 'Guardar información de cuenta'}
            >
              {deleteData ? (
                <div>
                  ¿Estás seguro de eliminar tu cuenta y toda la información relacionada directamente con ella?
                  <div className="text-greyDark">Esta acción es irreversible y no se puede deshacer.</div>
                </div>
              ) : (
                '¿Estás seguro de guardar los cambios?'
              )}
            </ModalConfirm>
          </form>
          <DevTool control={form.control} />
        </FormProvider>
      </div>
    </div>
  )
}

export default Settings
